import React, { useEffect, useState } from 'react';
import useClickOutside from '../ui/hooks/useClickOutside';
import { PremiumInputContainer } from '../ui/inputs/ui/PremiumInputContainer';
import { DropdownContainer } from '../ui/inputs/ui/DropdownContainer';
import { DropdownItem } from '../ui/inputs/ui/DropdownItem';
import { LabelAndValue } from '../ui/inputs/ui/LabelAndValue';
import { PremiumLabelSmall } from '../ui/inputs/ui/PremiumLabelSmall';
import { Invisible } from '../ui/inputs/ui/Invisible';
import { Value } from '../ui/inputs/ui/Value';
import { PremiumUnit } from '../ui/inputs/ui/PremiumUnit';
import { UnitsForLabelAndValue } from '../ui/inputs/ui/UnitsForLabelAndValue';
import { PremiumLabelBig } from '../ui/inputs/ui/PremiumLabelBig';
import BasicLoader from '../ui/loaders/basicLoader';
import { PremiumInput } from '../ui/inputs/customInput';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { RequiredIndicator } from '../ui/inputs/ui/RequiredIndicator';
import { SearchContainer } from '../ui/containers/searchContainer';
import { ItemsContainer } from '../ui/containers/itemsContainer';
import { ClearButton } from '../ui/buttons/ClearButton';
import ArrowDown from '../ui/icon/arrowDown';
import ticketService from 'modules/emergencies/ticketService';

const normalizeTextForSearch = (text) => {
  return (
    text
      //   ignore case
      .toUpperCase()
      // ignore weird letters
      .normalize('NFD')
      // ignore accents
      .replace(/\p{Diacritic}/gu, '')
      // custom ignore for zipcode match
      .replace(/-| /g, '')
  );
};

const search = (source, text) => {
  return normalizeTextForSearch(source).includes(normalizeTextForSearch(text));
};

const getFilteredOptions = (options, filterString) => {
  if (filterString.trim() === '') return options || [];
  return options?.filter((option) => {
    return search(option.search, filterString);
  });
};

const SelectWithSearchInput = ({
  onFieldChange,
  onSearchChange,
  options,
  isLoadingOptions,
  value,
  form,
  field,
  label,
  searchLabel,
  optionsEmptyMssg,
  isClereable = false,
  isDisabled,
  isRequired = false,
  inputRef,
  initialValue = '',
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  const [displayedValue, setDisplayedValue] = useState('');
  //updates the displayed value in case the field is changed externally
  useEffect(() => {
    // Find the selected option from the full options list, not filtered ones
    const selectedOption = options?.find((option) => option.value === value);

    // If we have a selected option, use its display text
    // If not, but we need to keep the current displayed value
    let valueToDisplay = selectedOption?.display || (value && displayedValue ? displayedValue : '');

    setDisplayedValue(valueToDisplay);
  }, [displayedValue, options, value]);

  const clickRefFrom = React.useRef();
  useClickOutside(clickRefFrom, () => {
    setHasFocus(false);
  });
  const [optionFilterString, setOptionFilterString] = useState(initialValue);
  const filteredOptions = getFilteredOptions(options, optionFilterString);
  const onFilterChange = (value) => {
    onSearchChange && onSearchChange(value);
    setOptionFilterString(value);
  };
  const changeField = (field, value, selectedOption) => {
    if (onFieldChange !== undefined) {
      onFieldChange(value, form, selectedOption);
    } else {
      form.setFieldValue(field.name, value);
    }
  };

  const cleanPropsForInput = ticketService.utils.clearPropsForInput(props);

  return (
    <PremiumInputContainer
      width={props.width}
      hasFocus={hasFocus}
      ref={clickRefFrom}
      onClick={() => {
        !isDisabled && setHasFocus(true);
      }}
      isDisabled={isDisabled}
      {...props}
    >
      {hasFocus && (
        <DropdownContainer maxHeight={'250px'}>
          <SearchContainer>
            <PremiumInput
              autoFocus
              onChange={(e) => {
                onFilterChange(e.target.value);
              }}
              type="search"
              value={optionFilterString}
              label={searchLabel}
            />
            {!!isClereable && value !== '' && (
              <ClearButton
                onClick={() => {
                  onFilterChange('');
                  changeField(field, '');
                  setDisplayedValue('');
                }}
              >
                <CloseIcon />
              </ClearButton>
            )}
          </SearchContainer>
          <ItemsContainer>
            {isLoadingOptions && (
              <DropdownItem hasPlaceholder>
                <BasicLoader size={10} />
              </DropdownItem>
            )}
            {!isLoadingOptions && filteredOptions.length === 0 && (
              <DropdownItem hasPlaceholder>{optionsEmptyMssg}</DropdownItem>
            )}
            {filteredOptions.length > 0 && (
              <>
                {filteredOptions.map((item, i) => (
                  <DropdownItem
                    key={`option-${i}`}
                    isSelected={item.value === value}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHasFocus(false);
                      onFilterChange('');
                      changeField(field, item.value, item);
                      setDisplayedValue(item.display);
                    }}
                  >
                    {item.label}
                  </DropdownItem>
                ))}
              </>
            )}
          </ItemsContainer>
        </DropdownContainer>
      )}
      {displayedValue ? (
        <LabelAndValue>
          <PremiumLabelSmall>
            {label}
            {isRequired && <RequiredIndicator />}
          </PremiumLabelSmall>
          <Value>
            <input
              ref={inputRef}
              disabled={isDisabled}
              value={displayedValue}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              readOnly={true}
              {...cleanPropsForInput}
            />
          </Value>
        </LabelAndValue>
      ) : (
        <>
          <PremiumLabelBig>
            {label}
            {isRequired && <RequiredIndicator />}
          </PremiumLabelBig>
          <Invisible>
            <input
              ref={inputRef}
              disabled={isDisabled}
              value={displayedValue}
              readOnly={true}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              {...cleanPropsForInput}
            />
          </Invisible>
          <UnitsForLabelAndValue noPadding={true}>
            <PremiumUnit>
              <ArrowDown />
            </PremiumUnit>
          </UnitsForLabelAndValue>
        </>
      )}
    </PremiumInputContainer>
  );
};
export default SelectWithSearchInput;
